'use client'

import Link from 'next/link'
import {useRouter} from 'next/navigation'
import {
  createContext,
  useContext,
  useTransition,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
  type TransitionStartFunction
} from 'react'
import {useTranslations} from 'next-intl'
import {Skeleton} from '@/components/Skeleton'
import './search-pending.css'

type SearchTransition = readonly [pending: boolean, start: TransitionStartFunction]

const SearchTransitionContext = createContext<SearchTransition | null>(null)

/**
 * One transition shared by the filter panel and the results below it.
 *
 * The panel starts the navigation and the results show the loader, and the
 * two are siblings on the page — so the pending flag lives above both
 * rather than in the panel, where the results could never see it.
 */
export function SearchPendingProvider({children}: {children: ReactNode}) {
  const transition = useTransition()
  return (
    <SearchTransitionContext.Provider value={transition}>
      {children}
    </SearchTransitionContext.Provider>
  )
}

/**
 * The shared search transition, or `null` outside a provider — the panel
 * falls back to its own transition then, so it still works on its own.
 */
export function useSearchTransition(): SearchTransition | null {
  return useContext(SearchTransitionContext)
}

/**
 * A link to another page of search results — a tab or "Show more" — that
 * shows the loader while that page is on its way.
 *
 * Still a real link: it works before JavaScript loads, and a modified click
 * (new tab, new window) is left to the browser rather than hijacked.
 */
export function SearchLink({
  href,
  onClick,
  ...props
}: ComponentProps<typeof Link> & {href: string}) {
  const router = useRouter()
  const start = useSearchTransition()?.[1]

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event)
    if (
      !start ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    event.preventDefault()
    // `scroll={false}` on "Show more" keeps the student where they were
    // reading instead of jumping back to the top of the list.
    start(() => router.push(href, {scroll: props.scroll !== false}))
  }

  return <Link href={href} onClick={handleClick} {...props} />
}

/**
 * The results, swapped for a loader while a new search is on its way.
 *
 * Skeleton cards in the shape of the real ones keep the page from jumping
 * when the results land; the status line says what is happening, because a
 * search that also looks on the web can take a few seconds.
 */
export function SearchResultsRegion({children}: {children: ReactNode}) {
  const pending = useSearchTransition()?.[0] ?? false
  const t = useTranslations('search')

  if (!pending) return <>{children}</>

  return (
    <div className="u-search-pending" aria-busy="true" data-testid="search-loader">
      <div className="u-search-pending__bar" aria-hidden="true" />
      <div className="u-search-pending__status" role="status">
        <span className="u-search-pending__orbit" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="u-search-pending__text">
          <strong>{t('searching')}</strong>
          <span>{t('searchingHelp')}</span>
        </span>
      </div>
      <ul className="u-opp-list" aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <li
            key={index}
            className="u-card u-search-pending__card"
            style={{animationDelay: `${index * 90}ms`}}
          >
            <Skeleton height={20} width={`${68 - index * 7}%`} />
            <Skeleton height={14} width="42%" />
            <Skeleton height={14} width="30%" />
            <div className="u-search-pending__tags">
              <Skeleton height={24} width={72} radius="var(--radius-full)" />
              <Skeleton height={24} width={96} radius="var(--radius-full)" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
